import { map, Observable, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { AupDetails, AupItem, AupReservation, AupReservationFilters, SelectedTableRow } from '../model/observer.model';
import { altitudeArrayObserver, getAltitudeIndex } from '../model/altitudes.model';
import { normalizeFromAupTime, normalizeUntilAupTime } from '../shared/utils/time.utils';
import { AupMapService } from '../map/services/aup-map.service';

const url = `/api/uup`;

@Injectable({
  providedIn: 'root'
})
export class ObserverService {
  private http = inject(HttpClient);
  private aupMapService = inject(AupMapService);
  aupDetails: WritableSignal<AupDetails> = signal({
    dateFrom: '',
    dateTo: '',
    name: 'AUP',
    status: '',
    reservationShortDTOS: [],
    designatorGroups: {}
  });

  selectedRow: WritableSignal<SelectedTableRow | null> = signal(null);

  filtersSignal: WritableSignal<AupReservationFilters> = signal({
    designator: [],
    responsibleUnit: [],
    flMin: altitudeArrayObserver[0],
    flMax: altitudeArrayObserver[altitudeArrayObserver.length - 1],
    from: '06:00',
    until: '06:00',
    remarks: ''
  });

  filteredReservation: Signal<AupReservation[]> = computed(() => {
    const filters = this.filtersSignal();
    return this.filterReservation(this.aupDetails().reservationShortDTOS, filters);
  });

  fetchAupList(): Observable<AupItem[]> {
    return this.http.get<AupItem[]>(`${url}/getAupLastStatus`);
  }

  fetchAupDetails(dates: { localDate: string }[]): Observable<AupDetails> {
    return this.http.post<AupDetails[]>(`${url}/findUpByDates`, dates).pipe(
      tap((details) => {
        this.aupDetails.set(details[0]);
      }),
      map((res: AupDetails[]) => res[0])
    );
  }

  filterReservation(reservations: AupReservation[], filters: AupReservationFilters): AupReservation[] {
    const filtered = reservations.filter((item) => {
      const matchesDesignator = this.matchesDesignator(item, filters);
      const matchesUnit =
        filters.responsibleUnit && filters.responsibleUnit.length
          ? filters.responsibleUnit.includes(item.responsibleUnit)
          : true;
      const matchesFromUntil = this.matchesFromUntil(item, filters);
      const matchesFlMinMax = this.matchesFlMinMax(item, filters);
      const matchesRemarks = filters.remarks?.length
        ? item.remarks?.toLowerCase().includes(filters.remarks.toLowerCase())
        : true;
      const matchesStatus = this.matchesStatus(item, filters);
      return matchesDesignator && matchesUnit && matchesFlMinMax && matchesFromUntil && matchesRemarks && matchesStatus;
    });
    return filtered;
  }

  updateFilters(filters: AupReservationFilters | null) {
    this.aupMapService.filterMapLayers(filters);
    if (filters) {
      this.filtersSignal.set(filters);
    } else {
      this.filtersSignal.set({
        designator: [],
        responsibleUnit: [],
        flMin: altitudeArrayObserver[0],
        flMax: altitudeArrayObserver[altitudeArrayObserver.length - 1],
        from: '06:00',
        until: '06:00',
        remarks: '',
        status: 'ALL'
      });
    }
  }

  refreshReservation() {
    this.filtersSignal.set({ ...this.filtersSignal() });
  }

  private matchesDesignator(layer: AupReservation, filters: AupReservationFilters): boolean {
    const filterRecord = (designatorBase: string, designatorSuffix: string, selectedOption: string, prefix: string) => {
      if (!designatorBase.startsWith(prefix) || !selectedOption.startsWith(prefix)) {
        return false;
      }

      if (designatorSuffix === '') {
        return designatorBase === selectedOption;
      }
      const optionSuffix = selectedOption.slice(prefix.length);
      return designatorSuffix.includes(optionSuffix) && designatorBase === prefix;
    };

    if (filters.designator?.length) {
      return filters.designator.some((designator) =>
        filterRecord(layer.designatorBase, layer.designatorSuffix, designator.name, designator.prefix || '')
      );
    }

    return true;
  }

  private matchesFlMinMax(reservation: AupReservation, filters: AupReservationFilters): boolean {
    const flMinFilter = filters.flMin ? getAltitudeIndex(filters.flMin) : getAltitudeIndex('GND');
    const flMaxFilter = filters.flMax ? getAltitudeIndex(filters.flMax) : getAltitudeIndex('UNL');

    const itemFlMin = getAltitudeIndex(reservation.flMin);
    const itemFlMax = getAltitudeIndex(reservation.flMax);
    return (
      (itemFlMin >= flMinFilter && itemFlMin <= flMaxFilter) ||
      (itemFlMax >= flMinFilter && itemFlMax <= flMaxFilter) ||
      (itemFlMin < flMinFilter && itemFlMax > flMaxFilter)
    );
  }

  private matchesFromUntil(reservation: AupReservation, filters: AupReservationFilters): boolean {
    const filterFrom = filters.from ? filters.from.replace(/_/g, '0') : '06:00';
    const filterUntil = filters.until ? filters.until.replace(/_/g, '0') : '06:00';
    if (
      (filterFrom === '06:00' && filterUntil === '06:00') ||
      (reservation.from === '06:00' && reservation.until === '06:00')
    )
      return true;

    const filterFromMinutes = normalizeFromAupTime(filterFrom);
    const filterUntilMinutes = normalizeUntilAupTime(filterUntil);
    const fromMinutes = normalizeFromAupTime(reservation.from);
    const untilMinutes = normalizeUntilAupTime(reservation.until);
    return (
      (fromMinutes >= filterFromMinutes && fromMinutes <= filterUntilMinutes) ||
      (untilMinutes >= filterFromMinutes && untilMinutes <= filterUntilMinutes)
    );
  }

  private matchesStatus(reservation: AupReservation, filters: AupReservationFilters): boolean {
    const status = filters.status !== 'ALL' && filters.status?.length ? filters.status : null;

    return status ? status.toLowerCase() === reservation.status.toLowerCase() : true;
  }
}
